"use client";

import React, { useEffect, useState, useCallback } from "react";
import { Quote, CircleCheck, ArrowLeft, ArrowRight } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function WhatOurClientsSay() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const testimonials = [{
    author: "Robert T.",
    quote: "Olive Hill got our Miller Deltaweld back on the line in record time. Their circuit board repair saved us thousands compared to a full replacement.",
    position: "Shop Foreman, Heavy Fab Inc."
  }, {
    author: "Marcus V.",
    quote: "Expert Hypertherm service. They diagnosed a gas flow issue on our Powermax that three other shops missed. Precise, professional, and competent.",
    position: "Production Manager, Precision Steel"
  }, {
    author: "Michael R.",
    quote: "Our Lincoln fleet depends on Olive Hill. They understand that downtime isn't an option in our industry. Best technical support in the region.",
    position: "Maintenance Lead, Rail Systems"
  }, {
    author: "Sarah L.",
    quote: "The technical expertise here is unmatched. They don't just swap parts; they actually repair the electronics. true industrial specialists.",
    position: "Owner, Custom Weld Works"
  }];
  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);
  const handlePrev = useCallback(() => {
    api?.scrollPrev();
  }, [api]);
  const handleNext = useCallback(() => {
    api?.scrollNext();
  }, [api]);
  const progressValue = count > 0 ? current / count * 100 : 0;
  return <section data-section-id="33247" id="field-reports" ref={ref} className={cn("dark bg-background text-foreground relative overflow-hidden py-24 md:py-32 transition-opacity duration-700", isVisible ? "opacity-100" : "opacity-0")}>
      {}
      <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 -translate-x-1/2 -translate-y-1/2 select-none opacity-[0.03]">
        <span className="font-sans text-[40rem] font-black leading-none text-primary">
          O
        </span>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="mx-auto max-w-4xl">
          <Carousel setApi={setApi} className="w-full">
            <CarouselContent>
              {testimonials.map((item, i) => <CarouselItem key={i} data-index={i}>
                  <div className="flex flex-col items-center text-center px-4 md:px-12">
                    {}
                    <div className="mb-8 rounded-sm bg-primary/10 p-4 text-primary">
                      <Quote className="size-8 md:size-10 fill-current" />
                    </div>

                    {}
                    <blockquote className="mb-10 text-2xl font-serif italic font-medium leading-tight tracking-tight md:text-4xl lg:text-5xl text-foreground">
                      &ldquo;{item.quote}&rdquo;
                    </blockquote>

                    {}
                    <div className="flex flex-col items-center gap-4">
                      <div className="text-center">
                        <cite className="block text-xl font-bold not-italic text-foreground uppercase tracking-tight">
                          {item.author}
                        </cite>
                        <span className="text-xs font-mono font-medium text-muted-foreground uppercase tracking-widest mt-1 block">
                          {item.position}
                        </span>
                      </div>

                      {}
                      <Badge variant="secondary" className="mt-2 gap-1.5 bg-primary/10 text-primary hover:bg-primary/20 border-primary/20 px-3 py-1 rounded-sm">
                        <CircleCheck className="size-3.5" />
                        Certified Repair Report
                      </Badge>
                    </div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
          </Carousel>

          {}
          <div className="mt-16 flex flex-col items-center justify-center gap-6">
            <div className="flex items-center gap-4 w-full max-w-xs">
              <Button variant="ghost" size="icon" onClick={handlePrev} className="rounded-sm text-primary hover:bg-primary/10 hover:text-primary transition-colors" aria-label="Previous testimonial">
                <ArrowLeft className="size-6" />
              </Button>

              <div className="flex-1">
                <Progress value={progressValue} className="h-1 bg-muted [&>div]:bg-primary" />
              </div>

              <Button variant="ghost" size="icon" onClick={handleNext} className="rounded-sm text-primary hover:bg-primary/10 hover:text-primary transition-colors" aria-label="Next testimonial">
                <ArrowRight className="size-6" />
              </Button>
            </div>
            <div className="text-xs font-mono font-medium text-muted-foreground tabular-nums tracking-widest">
              {current.toString().padStart(2, "0")} /{" "}
              {count.toString().padStart(2, "0")}
            </div>
          </div>
        </div>
      </div>
    </section>;
}